import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { Badge, Button, Spinner, Table, useToast } from "@/components/ds";
import { AdminPage } from "@/components/app/admin/AdminPage";
import { usePageTitle } from "../hooks/usePageTitle";
import styles from "./AdminOrgDetailPage.module.css";

interface OrgDetail {
  readonly id: string;
  readonly name: string;
  readonly plan: string;
  readonly subscriptionStatus: string;
  readonly trialEndsAt: string;
  readonly suspendedAt: string | null;
  readonly suspensionReason: string | null;
  readonly stripeCustomerId: string | null;
  readonly stripeSubscriptionId: string | null;
  readonly billingSyncDueAt: string | null;
  readonly cleanerCount: number;
  readonly monthlyEstimateGbp: number;
  readonly billingState: { readonly label: string; readonly trialDaysRemaining: number | null };
}

interface MemberItem {
  readonly membershipId: string;
  readonly name: string;
  readonly email: string;
  readonly status: string;
  readonly isOwner: boolean;
  readonly isCleaner: boolean;
  readonly isAccountCreator: boolean;
}

interface AuditEntry {
  readonly id: string;
  readonly field: string;
  readonly oldValue: string | null;
  readonly newValue: string | null;
  readonly source: string;
  readonly reason: string | null;
  readonly createdAt: string;
}

interface EventItem {
  readonly id: string;
  readonly type: string;
  readonly status: string;
  readonly receivedAt: string;
  readonly processedAt: string | null;
  readonly error: string | null;
}

interface StripeCustomerData {
  readonly id: string;
  readonly email: string | null;
  readonly defaultPaymentMethodBrand: string | null;
  readonly defaultPaymentMethodLast4: string | null;
}

interface StripeSubscriptionData {
  readonly id: string;
  readonly status: string;
  readonly items: readonly { readonly id: string; readonly priceId: string; readonly quantity: number }[];
  readonly cancelAtPeriodEnd: boolean;
  readonly currentPeriodEnd: string;
}

interface OrgDetailResponse {
  readonly org: OrgDetail;
  readonly members: readonly MemberItem[];
  readonly audit: readonly AuditEntry[];
  readonly events: readonly EventItem[];
  readonly stripeCustomer: StripeCustomerData | null;
  readonly stripeSubscription: StripeSubscriptionData | null;
  readonly stripeError: string | null;
}

type State =
  | { kind: "loading" }
  | { kind: "unauthorized" }
  | { kind: "ready"; data: OrgDetailResponse }
  | { kind: "error"; message: string };

function labelVariant(label: string): "success" | "warning" | "danger" | "neutral" | "info" {
  if (label === "active") return "success";
  if (label === "trial_ending" || label === "past_due" || label === "trial_expired") return "warning";
  if (label === "suspended") return "danger";
  if (label === "cancelled") return "info";
  return "neutral";
}

function eventVariant(status: string): "success" | "warning" | "danger" | "neutral" {
  if (status === "processed") return "success";
  if (status === "pending") return "warning";
  if (status === "failed") return "danger";
  return "neutral";
}

export function AdminOrgDetailPage() {
  const { id } = useParams<{ id: string }>();
  usePageTitle("Admin · Organisation");
  const [state, setState] = useState<State>({ kind: "loading" });
  const [actionLoading, setActionLoading] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [extendDays, setExtendDays] = useState("7");
  const toast = useToast();

  const load = useCallback(async () => {
    if (!id) return;
    setState({ kind: "loading" });
    try {
      const res = await fetch(`/api/admin/orgs/${id}`);
      if (res.status === 404) { setState({ kind: "unauthorized" }); return; }
      if (!res.ok) { setState({ kind: "error", message: `Failed (${res.status})` }); return; }
      const json = await res.json() as { success: boolean; data?: OrgDetailResponse };
      if (json.success && json.data) setState({ kind: "ready", data: json.data });
      else setState({ kind: "error", message: "Unexpected response" });
    } catch {
      setState({ kind: "error", message: "Network error" });
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function runAction(path: string, body?: unknown): Promise<void> {
    setActionLoading(true);
    try {
      const res = await fetch(`/api/admin/orgs/${id}${path}`, {
        method: "POST",
        headers: body ? { "Content-Type": "application/json" } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      const json = await res.json() as { success: boolean; error?: { message: string } };
      if (!json.success) {
        toast.toast("error", json.error?.message ?? "Action failed");
      } else {
        toast.toast("success", "Done");
        await load();
      }
    } catch (error) {
      toast.toast("error", error instanceof Error ? error.message : "Network error");
    } finally {
      setActionLoading(false);
    }
  }

  if (state.kind === "loading") return <AdminPage title="Loading…"><Spinner /></AdminPage>;
  if (state.kind === "unauthorized") return <AdminPage title="Not found" showBack={false}><div /></AdminPage>;
  if (state.kind === "error") return <AdminPage title="Error"><div>{state.message}</div></AdminPage>;

  const { org, members, audit, events, stripeCustomer, stripeSubscription, stripeError } = state.data;
  const cleanerItem = stripeSubscription?.items.find((i) => i.priceId.includes("cleaner"));
  const stripeQuantity = cleanerItem?.quantity;
  const quantityDrift = stripeQuantity !== undefined && stripeQuantity !== org.cleanerCount;
  const hasStripe = !!org.stripeCustomerId || !!org.stripeSubscriptionId;

  return (
    <AdminPage title={org.name} aside={<span className={styles.id}>{org.id}</span>}>
      <div className={styles.stats}>
        <div>
          <span className={styles.label}>State</span>
          <span><Badge variant={labelVariant(org.billingState.label)}>{org.billingState.label.replace(/_/g, " ")}</Badge></span>
        </div>
        <div>
          <span className={styles.label}>Plan</span>
          <b>{org.plan}</b>
        </div>
        <div>
          <span className={styles.label}>Status</span>
          <b>{org.subscriptionStatus}</b>
        </div>
        <div>
          <span className={styles.label}>Cleaners</span>
          <b>{org.cleanerCount}</b>
        </div>
        <div>
          <span className={styles.label}>Monthly</span>
          <b>£{org.monthlyEstimateGbp.toFixed(2)}</b>
        </div>
        <div>
          <span className={styles.label}>Trial ends</span>
          <b>{new Date(org.trialEndsAt).toLocaleDateString("en-GB")}</b>
        </div>
        {org.billingSyncDueAt ? (
          <div>
            <span className={styles.label}>Sync due</span>
            <b>{new Date(org.billingSyncDueAt).toLocaleTimeString()}</b>
          </div>
        ) : null}
      </div>

      {org.suspendedAt ? (
        <div className={styles.suspended}>
          <div className={styles.suspendedText}>
            <strong>Suspended</strong> since {new Date(org.suspendedAt).toLocaleString()}
            {org.suspensionReason ? <> · {org.suspensionReason}</> : null}
          </div>
          <Button onClick={() => runAction("/reactivate")} disabled={actionLoading}>Reactivate</Button>
        </div>
      ) : (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Actions</h2>
          <div className={styles.actionRow}>
            <input
              type="number"
              min={1}
              max={180}
              value={extendDays}
              onChange={(e) => setExtendDays(e.target.value)}
              className={`${styles.input} ${styles.inputNumber}`}
              aria-label="Days"
            />
            <Button variant="secondary" onClick={() => runAction("/extend-trial", { days: Number(extendDays) })} disabled={actionLoading}>
              Extend trial
            </Button>
            <Button variant="secondary" onClick={() => runAction("/refresh-from-stripe")} disabled={actionLoading || !org.stripeSubscriptionId}>
              Refresh from Stripe
            </Button>
            <Button variant="secondary" onClick={() => runAction("/sync-to-stripe")} disabled={actionLoading || !org.stripeSubscriptionId}>
              Sync to Stripe
            </Button>
          </div>
          <div className={styles.actionRow}>
            <input
              type="text"
              placeholder="Reason"
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              className={`${styles.input} ${styles.inputReason}`}
            />
            <Button variant="destructive" onClick={() => runAction("/suspend", { reason: suspendReason })} disabled={actionLoading || !suspendReason.trim()}>
              Suspend
            </Button>
          </div>
        </div>
      )}

      {hasStripe ? (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Stripe</h2>
          {stripeError ? (
            <div className={styles.help}>{stripeError}</div>
          ) : (
            <div className={styles.stripe}>
              <span className={styles.label}>Customer</span>
              <span className={styles.value}>{stripeCustomer?.id ?? org.stripeCustomerId ?? "—"}</span>
              <span className={styles.label}>Subscription</span>
              <span className={styles.value}>{stripeSubscription?.id ?? org.stripeSubscriptionId ?? "—"}</span>
              {stripeSubscription ? (<>
                <span className={styles.label}>Status</span>
                <span className={styles.valueText}>{stripeSubscription.status}</span>
              </>) : null}
              {stripeQuantity !== undefined ? (<>
                <span className={styles.label}>Cleaner qty</span>
                <span className={styles.valueText}>
                  {stripeQuantity}
                  {quantityDrift ? <span className={styles.drift}>drift · local {org.cleanerCount}</span> : null}
                </span>
              </>) : null}
              {stripeCustomer?.defaultPaymentMethodLast4 ? (<>
                <span className={styles.label}>Card</span>
                <span className={styles.valueText}>
                  {stripeCustomer.defaultPaymentMethodBrand ?? "Card"} ending {stripeCustomer.defaultPaymentMethodLast4}
                </span>
              </>) : null}
            </div>
          )}
        </div>
      ) : null}

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Members · {members.length}</h2>
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Email</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Roles</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {members.map((m) => (
              <Table.Row key={m.membershipId}>
                <Table.Cell>
                  {m.name}
                  {m.isAccountCreator ? <> <Badge variant="info">Creator</Badge></> : null}
                </Table.Cell>
                <Table.Cell>{m.email}</Table.Cell>
                <Table.Cell>{m.status}</Table.Cell>
                <Table.Cell>
                  {m.isOwner && m.isCleaner ? "Manager · Cleaner" : m.isOwner ? "Manager" : m.isCleaner ? "Cleaner" : ""}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>

      {events.length > 0 ? (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Stripe events · {events.length}</h2>
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Received</Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Error</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {events.map((e) => (
                <Table.Row key={e.id}>
                  <Table.Cell>{new Date(e.receivedAt).toLocaleString()}</Table.Cell>
                  <Table.Cell>{e.type}</Table.Cell>
                  <Table.Cell><Badge variant={eventVariant(e.status)}>{e.status}</Badge></Table.Cell>
                  <Table.Cell>{e.error ?? ""}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      ) : null}

      {audit.length > 0 ? (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Audit · {audit.length}</h2>
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>When</Table.HeaderCell>
                <Table.HeaderCell>Field</Table.HeaderCell>
                <Table.HeaderCell>From</Table.HeaderCell>
                <Table.HeaderCell>To</Table.HeaderCell>
                <Table.HeaderCell>Source</Table.HeaderCell>
                <Table.HeaderCell>Reason</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {audit.map((a) => (
                <Table.Row key={a.id}>
                  <Table.Cell>{new Date(a.createdAt).toLocaleString()}</Table.Cell>
                  <Table.Cell>{a.field}</Table.Cell>
                  <Table.Cell>{a.oldValue ?? "—"}</Table.Cell>
                  <Table.Cell>{a.newValue ?? "—"}</Table.Cell>
                  <Table.Cell>{a.source}</Table.Cell>
                  <Table.Cell>{a.reason ?? ""}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      ) : null}
    </AdminPage>
  );
}
